import React, { useState, Fragment, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Input,
  Label,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFile } from "api/mutators/file";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

const defaultValues = {
  name: "",
  description: "",
  file: "",
  view: "ALL",
};

const FileModal = (props: any) => {
  const { t } = useTranslation("translation", { keyPrefix: "FILE_MANAGER" });

  const schema = yup.object().shape({
    name: yup.string().required(t("ERRORS:TITLE")),
    description: yup.string(),
    file: yup
      .mixed()
      .required("Il file è obbligatorio")
      .test("fileType", `${t("ERRORS:FILE_NOT_VALID")}`, (value: any) => {
        if (!value) return false;
        const allowedTypes = ["application/pdf", "video/mp4"];
        return allowedTypes.includes(value.type);
      })
      .test("fileSize", `${t("ERRORS:SIZE")}`, (value: any) => {
        if (!value) return true;
        const maxSize = 4 * 1024 * 1024 * 1024; // 4GB
        return value.size <= maxSize;
      }),
    view: yup.string().required(t("ERRORS:TITLE")),
  });

  const { modal, toggle, setModal, refetchFiles, folderId, folderView } = props;

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createFile,
    onError: (error: any) => {
      queryClient.invalidateQueries({
        queryKey: ["fetchMemoryItemsInFolderForTable"],
      });
      queryClient.invalidateQueries({ queryKey: ["fetchMemoryItemsInFolder"] });
      toast.error("Error! File not created", { autoClose: 3000 });
    },
    onSuccess: () => {
      setModal(false);
      reset(defaultValues);
      refetchFiles();
      queryClient.invalidateQueries({
        queryKey: ["fetchMemoryItemsInFolderForTable"],
      });
      queryClient.invalidateQueries({ queryKey: ["fetchMemoryItemsInFolder"] });
      toast.success("File Created Successfully", { autoClose: 3000 });
    },
  });

  const [file, setFile] = useState<any>(null);
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setValue("file", selectedFile);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/msword": [],
      "video/mp4": [],
    },
    multiple: false,
  });

  function onSubmit() {
    const formData = new FormData();
    formData.append("name", getValues().name);
    formData.append("description", getValues().description || "");
    formData.append("file", file);
    formData.append("parentFolderId", folderId);
    formData.append("view", getValues().view);
    mutation.mutate(formData);
    setFile(null);
  }

  useEffect(() => {
    if (folderView && folderView !== "ALL") {
      setValue("view", folderView);
    } else {
      setValue("view", "ALL");
    }
  }, [folderView, setValue]);

  return (
    <Fragment>
      <Modal id="showModal" size={"xl"} isOpen={modal} toggle={toggle} centered>
        <ModalHeader className="bg-light p-3" toggle={toggle}>
          {t("MODALS:ADD")}
        </ModalHeader>
        <Form className="tablelist-form" onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <>
              <Row>
                <Col lg={4}>
                  <div className="mb-3 pb-2">
                    <Label htmlFor="view" className="form-label">
                      {t("MODALS:VIEW")}
                    </Label>
                    <Controller
                      control={control}
                      name="view"
                      render={({ field }) => (
                        <select
                          {...field}
                          className="form-select"
                          id="view"
                          value={field.value || defaultValues.view}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={folderView !== "ALL"}
                        >
                          <option value="ALL">{t("MODALS:ALL")}</option>
                          <option value="CAREGIVER_PROFESSIONAL">
                            {t("MODALS:CAREGIVER_PROFESSIONAL")}
                          </option>
                          <option value="CAREGIVER_USER">
                            {t("MODALS:CAREGIVER_USER")}
                          </option>
                        </select>
                      )}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Label className="form-label" for="file">
                    {t("MODALS:UPLOAD")}
                  </Label>
                  <div
                    {...getRootProps({
                      className: `dropzone ${
                        isDragActive ? "dropzone-active" : ""
                      }`,
                      style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #ced4da",
                        borderRadius: "5px",
                        padding: "20px",
                        textAlign: "center",
                        cursor: "pointer",
                        color: "#b9bbc3",
                        height: "150px",
                      },
                    })}
                  >
                    <input {...getInputProps()} id="file" name="file" />
                    {isDragActive ? (
                      <p>{t("MODALS:RELEASE_FILE")}</p>
                    ) : file ? (
                      <p>{file.name}</p>
                    ) : (
                      <p>{t("MODALS:SELECT_FILE")}</p>
                    )}
                  </div>
                  {errors.file && (
                    <FormFeedback style={{ display: "block" }}>
                      {errors.file.message}
                    </FormFeedback>
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Label className="form-label" for="name">
                    {t("MODALS:NAME")}
                  </Label>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="name"
                        placeholder={t("MODALS:INSERT_TITLE")}
                        invalid={!!errors.name}
                      />
                    )}
                  />
                  {errors.name && (
                    <FormFeedback>{errors.name.message}</FormFeedback>
                  )}
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <div className="mb-3 pb-2">
                    <Label htmlFor="description" className="form-label">
                      {t("MODALS:DESCRIPTION")}
                    </Label>
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <textarea
                          {...field}
                          className="form-control"
                          id="description"
                          rows={3}
                        ></textarea>
                      )}
                    />
                  </div>
                </Col>
              </Row>
            </>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  reset(defaultValues);
                  setFile(null);
                  setModal(false);
                }}
              >
                {t("MODALS:CLOSE")}
              </button>
              <button type="submit" className="btn btn-soft-danger">
                {t("MODALS:SAVE")}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
      <ToastContainer closeButton={false} limit={1} />
    </Fragment>
  );
};

export default FileModal;
